import React, { useState, useMemo, useCallback } from "react";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
  useSortBy,
} from "react-table";
import useEmployer from "../../hooks/useEmployer";
import { ACCOUNT_STATUS, DELETE_EMPLOYER } from "../../utils/constants";
import { useSelector } from "react-redux";

const StatusToggle = ({ value, id, token }) => {
  const [isActive, setIsActive] = useState(value == 1);

  const handleClick = async () => {
    const newValue = !isActive;
    setIsActive(newValue);
    try {
      const formData = new FormData();
      formData.append("status", newValue ? 1 : 0);
      await fetch(`${ACCOUNT_STATUS}${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`relative w-12 h-6 rounded-full transition-colors ${isActive ? "bg-emerald-500" : "bg-gray-300"}`}
    >
      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${isActive ? "left-7" : "left-1"}`}></span>
    </button>
  );
};

const VerificationBadge = ({ value, type }) => {
  const isVerified = value === "verified";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
      isVerified ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isVerified ? "bg-emerald-500" : "bg-gray-400"}`}></span>
      {isVerified ? "Verified" : "Pending"}
    </span>
  );
};

const Employer = () => {
  const positions = useEmployer();
  const [successMessage, setSuccessMessage] = useState("");
  const user = useSelector((state) => state.user);

  const handleDelete = useCallback(async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this employer?");
    if (confirmDelete) {
      try {
        const { token } = user;
        const response = await fetch(`${DELETE_EMPLOYER}${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setSuccessMessage("Employer deleted successfully!");
          setTimeout(() => {
            setSuccessMessage("");
            window.location.reload();
          }, 1500);
        } else {
          alert("Failed to delete employer. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting employer:", error);
        alert("Failed to delete employer. Please try again.");
      }
    }
  }, [user]);

  const columns = useMemo(
    () => [
      {
        Header: "Employer Info",
        accessor: "employerInfo",
        Cell: ({ cell: { row } }) => {
          const { companylogo, email, name } = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center overflow-hidden">
                {companylogo ? (
                  <img src={companylogo} alt="Company" className="w-full h-full object-cover" />
                ) : (
                  <i className="bi bi-building text-violet-500"></i>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">{name || 'Unknown'}</div>
                <div className="text-sm text-gray-500">{row.original.company_name}</div>
                <div className="text-sm text-gray-500">{email}</div>
                <div className="text-sm text-gray-500">{row.original.mobile}</div>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Industry",
        accessor: "industry_type",
        sortType: "alphanumeric",
        Cell: ({ value }) => (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            {value || 'N/A'}
          </span>
        ),
      },
      {
        Header: "Active Jobs",
        accessor: "activeJob",
        sortType: "alphanumeric",
        Cell: ({ row }) => (
          <Link
            to={`/employer_details/${row.original.id}`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-medium text-sm hover:bg-blue-200 transition-colors"
          >
            {row.original.activeJob || 0}
          </Link>
        ),
      },
      {
        Header: "Established",
        accessor: "establishmentYear",
        sortType: "alphanumeric",
        Cell: ({ value }) => (
          <span className="text-gray-600 text-sm">{value || 'N/A'}</span>
        ),
      },
      {
        Header: "Account Status",
        accessor: "status",
        sortType: "alphanumeric",
        Cell: ({ row }) => (
          <StatusToggle value={row.original.status} id={row.original.id} token={user?.token} />
        ),
      },
      {
        Header: "OTP",
        accessor: "otp_verified",
        sortType: "alphanumeric",
        Cell: ({ row }) => <VerificationBadge value={row.original.otp_verified} type="otp" />,
      },
      {
        Header: "Profile",
        accessor: "profile_verified",
        sortType: "alphanumeric",
        Cell: ({ row }) => <VerificationBadge value={row.original.profile_verified} type="profile" />,
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              to={`/employer_details/${row.values.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-eye"></i>
              View
            </Link>
            <Link
              to={`/edit_employer/${row.values.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-pencil"></i>
              Edit
            </Link>
            <button
              onClick={() => handleDelete(row.values.id)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-trash"></i>
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleDelete, user?.token]
  );

  const data = useMemo(() => positions || [], [positions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    setPageSize,
  } = useTable(
    { columns, data, initialState: { pageSize: 10, sortBy: [{ id: "establishmentYear", desc: true }] } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow-lg">
                <i className="bi bi-building text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">
                  Employers
                </h1>
                <p className="text-gray-500">Manage companies and their job postings</p>
              </div>
            </div>
            <Link
              to="/create_employer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl hover:from-violet-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
            >
              <i className="bi bi-plus-lg"></i>
              Add Employer
            </Link>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
            <i className="bi bi-check-circle-fill text-emerald-500 text-xl"></i>
            <span className="text-emerald-700 font-medium">{successMessage}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
            <div className="relative">
              <i className="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search employers..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl w-72 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">entries</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" {...getTableProps()}>
              <thead className="bg-gradient-to-r from-violet-50 to-purple-50">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-violet-100/50 transition-colors"
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        <div className="flex items-center gap-2">
                          {column.render("Header")}
                          <span className="text-violet-400">
                            {column.isSorted ? (column.isSortedDesc ? "↓" : "↑") : ""}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100" {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="bi bi-building text-4xl text-gray-300"></i>
                        <p className="text-gray-500">No employers found</p>
                        <Link to="/create_employer" className="text-violet-500 hover:underline">
                          Add your first employer
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr className="hover:bg-violet-50/30 transition-colors" {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td className="px-6 py-4 text-sm text-gray-700" {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {page.length > 0 && (
            <div className="p-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{pageIndex * pageSize + 1}</span> to{" "}
                <span className="font-medium">{Math.min((pageIndex + 1) * pageSize, data.length)}</span>{" "}
                of <span className="font-medium">{data.length}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {pageOptions.slice(Math.max(0, pageIndex - 2), Math.min(pageOptions.length, pageIndex + 3)).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => gotoPage(pg)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${pageIndex === pg ? "bg-violet-500 text-white" : "hover:bg-gray-100 text-gray-600"}`}
                    >
                      {pg + 1}
                    </button>
                  ))}
                </div>
                <button onClick={() => nextPage()} disabled={!canNextPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Next
                </button>
                <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(Employer);
